"use client";

import React from "react";
import { TrendingUp, Sun, Briefcase, Banknote, Calendar, Users } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const benefits = [{
  title: "Comprehensive Reports",
  description: "Receive detailed, easy-to-read digital reports within 24 hours, featuring high-resolution photos and clear explanations.",
  icon: TrendingUp,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/inspection_report.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/inspection_report.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/inspection_report.png-wvc-sizes",
    alt: "Detailed home inspection report"
  }
}, {
  title: "Certified Expertise",
  description: "Our InterNACHI certified inspectors bring years of local experience to every property, providing a detailed visual evaluation of accessible systems and components.",
  icon: Sun,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/certified_inspector.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/certified_inspector.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/certified_inspector.png-wvc-sizes",
    alt: "Certified home inspector at work"
  }
}, {
  title: "Advanced Technology",
  description: "We utilize thermal imaging and sewer cameras to detect hidden issues that standard visual inspections might miss.",
  icon: Briefcase,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/thermal_imaging.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/thermal_imaging.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/thermal_imaging.png-wvc-sizes",
    alt: "Thermal imaging camera inspection"
  }
}, {
  title: "Insurance Inspection Options",
  description: "Wind mitigation and 4-Point inspections are available when insurance documentation is needed. Eligibility and any available credits are determined by the insurance carrier.",
  icon: Banknote,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/insurance_savings.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/insurance_savings.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/insurance_savings.png-wvc-sizes",
    alt: "Home insurance documents"
  }
}, {
  title: "Flexible Scheduling",
  description: "We offer convenient appointment times throughout South Florida to accommodate your busy real estate closing timeline.",
  icon: Calendar,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/flexible_booking.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/flexible_booking.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/flexible_booking.png-wvc-sizes",
    alt: "Online booking calendar"
  }
}, {
  title: "Peace of Mind",
  description: "Buy or sell with greater confidence through a clearer understanding of the property’s visible and accessible systems and components.",
  icon: Users,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/happy_homeowner.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/happy_homeowner.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/happy_homeowner.png-wvc-sizes",
    alt: "Satisfied homeowners"
  }
}];
export default function OwnershipBenefits() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="248637" id="benefits" className="bg-background text-foreground py-16 md:py-24 lg:py-32">
      <div className="page-container">
        <div className="mb-12 text-center md:mb-16 lg:mb-20">
          <h2 className="font-default text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl text-secondary">
            The Trusted Inspections Advantage
          </h2>
          <p className="text-muted-foreground mx-auto mt-4 max-w-2xl text-lg font-default">
            We provide the clarity and professional insight you need to make informed decisions about your South Florida property.
          </p>
        </div>

        <div ref={ref} className={`grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {benefits.map((benefit, i) => {
          const Icon = benefit.icon;
          return <Card key={i} data-index={i} className="group overflow-hidden border-border bg-card shadow-sm transition-all duration-300 hover:border-primary/50 hover:shadow-xl hover:-translate-y-1">
                <div className="overflow-hidden">
                  <AspectRatio ratio={16 / 9}>
                    <img src={benefit.image.src} data-wvc-srcset={benefit.image.srcset} data-wvc-sizes={benefit.image.sizes} alt={benefit.image.alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </AspectRatio>
                </div>
                <CardHeader className="relative pb-2">
                  <div className="bg-background border-border absolute -top-6 left-6 flex h-12 w-12 items-center justify-center rounded-lg border shadow-md">
                    <Icon className="text-primary h-6 w-6" />
                  </div>
                  <CardTitle className="font-default mt-4 text-xl text-secondary">
                    {benefit.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="font-default text-base leading-relaxed text-muted-foreground">
                    {benefit.description}
                  </CardDescription>
                </CardContent>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}